import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import offensiveApi from '../../services/offensiveApi';
import RiskMatrix from '../../components/offensive/RiskMatrix';
import RiskHistory from '../../components/offensive/RiskHistory';
import { BAND_FILL, BAND_LABEL } from '../../components/offensive/riskBands';

const SEVERITY_TONE = {
  critical: 'text-red-400', 'crítico': 'text-red-400',
  high: 'text-orange-400', alto: 'text-orange-400',
  medium: 'text-yellow-400', medio: 'text-yellow-400',
  low: 'text-blue-400', bajo: 'text-blue-400',
};

/**
 * Results across offensive engagements.
 *
 * Built around the exposure × impact matrix rather than a grid of dials. The
 * matrix is what the reports carry, so it is what a reader already knows how to
 * read — and counts do not belong in gauges. The one gauge here is catalogue
 * coverage, which has a fixed denominator and a target of all of it.
 */

const SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'network', label: 'Network & Applications' },
  { id: 'wireless', label: 'Wireless' },
];

const Stat = ({ value, label, tone = 'text-slate-100', hint }) => (
  <div title={hint}>
    <div className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

const Panel = ({ title, children, aside }) => (
  <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-4">
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
      {aside && <span className="text-[11px] text-slate-500">{aside}</span>}
    </div>
    {children}
  </div>
);

function OffensiveDashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [scope, setScope] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The cell currently expanded, and what is behind it. Fetched on click
  // rather than shipped with the dashboard: twenty-five cells' worth of
  // findings would be paid for so that one could be opened.
  const [cell, setCell] = useState(null);
  const [cellFindings, setCellFindings] = useState(null);
  // Expanded, the breakdown takes the width the band charts were using. They
  // are a summary of the same findings, so borrowing their space to read the
  // findings themselves costs the reader nothing they were mid-way through.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    offensiveApi.getCustomers()
      .then((list) => setCustomers(Array.isArray(list) ? list : []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCell(null);
    setCellFindings(null);
    setExpanded(false);
    offensiveApi.getOffensiveDashboard(customerId || undefined, scope)
      .then((result) => { if (!cancelled) { setData(result); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, scope]);

  const selectCell = (next) => {
    // Clicking the open cell closes it, so the matrix is never stuck showing a
    // selection the reader has finished with.
    if (cell && cell.exposure === next.exposure && cell.impact === next.impact) {
      setCell(null);
      setCellFindings(null);
      setExpanded(false);
      return;
    }
    setCell(next);
    setCellFindings(null);
    offensiveApi.getDashboardCell(next.exposure, next.impact, customerId || undefined, scope)
      .then((result) => setCellFindings(result.findings || []))
      .catch(() => setCellFindings([]));
  };

  const totals = data?.totals || {};
  const bands = data?.bands || {};
  const bandMax = Math.max(1, ...Object.values(bands));
  const engagements = data?.engagements || [];
  const unestimated = (totals.findings || 0) - (totals.estimated || 0);

  // Coverage only means something where there is a fixed catalogue behind it.
  const wireless = engagements.filter((e) => e.coverage);
  const coverage = wireless.length
    ? {
        executed: wireless.reduce((n, e) => n + e.coverage.executed, 0),
        total: wireless.reduce((n, e) => n + e.coverage.total, 0),
      }
    : null;
  const coverageRatio = coverage && coverage.total ? coverage.executed / coverage.total : 0;

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-[96rem] mx-auto">

            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl text-slate-100 font-bold">Offensive Results</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Risk scored as exposure × impact — the same model the delivered reports use.
                </p>
              </div>

              <div className="flex items-center gap-4">
                <select
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')}
                  className="text-sm text-slate-300 bg-transparent border-0 border-b border-slate-700 focus:border-indigo-500 focus:ring-0 py-1 pr-6 pl-1 cursor-pointer"
                >
                  <option value="">All customers</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.short_name || c.name}</option>
                  ))}
                </select>

                <div className="inline-flex rounded-md border border-slate-800 overflow-hidden">
                  {SCOPES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setScope(s.id)}
                      className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        scope === s.id ? 'bg-slate-800 text-slate-200'
                                       : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-950/30 border border-red-500/40 text-xs text-slate-300">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
              </div>
            ) : !engagements.length ? (
              <div className="text-center py-20 text-sm text-slate-500">
                No offensive engagements for this selection.
              </div>
            ) : (
              <div className="space-y-6">

                {/* Numbers, not dials. A count in a gauge is a count with a
                    decoration; these earn their space by being read together. */}
                <div className="flex flex-wrap items-start gap-x-12 gap-y-4 px-4 py-4 rounded-lg bg-slate-900/50 border border-slate-800">
                  <Stat value={totals.engagements ?? 0} label="engagements" />
                  <Stat value={totals.findings ?? 0} label="findings" />
                  <Stat
                    value={(bands['CRÍTICO'] || 0) + (bands['ALTO'] || 0)}
                    label="critical & high"
                    tone="text-red-400"
                  />
                  <Stat
                    value={totals.unremediated ?? 0}
                    label="without remediation"
                    tone={totals.unremediated ? 'text-amber-400' : 'text-slate-100'}
                    hint="Findings with no remediation written — they cannot be delivered as they stand"
                  />
                  {unestimated > 0 && (
                    <Stat
                      value={unestimated}
                      label="without risk estimate"
                      tone="text-amber-400"
                      hint="Not placed on the matrix. Draft Annex B estimation for these."
                    />
                  )}
                  {coverage && (
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-semibold tabular-nums text-slate-100">
                        {Math.round(coverageRatio * 100)}%
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        catalogue executed ({coverage.executed}/{coverage.total})
                      </div>
                      <div className="mt-1.5 h-1.5 w-40 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all duration-700"
                          style={{ width: `${coverageRatio * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className={expanded ? 'lg:col-span-3' : 'lg:col-span-2'}>
                    <Panel
                      title="Risk matrix"
                      aside={unestimated > 0
                        ? `${totals.estimated} of ${totals.findings} findings estimated`
                        : `${totals.findings} findings`}
                    >
                      <div className="flex flex-col xl:flex-row gap-6">
                        <div className="xl:w-[26rem] shrink-0">
                          <RiskMatrix
                            cells={data.matrix}
                            selected={cell}
                            onSelect={selectCell}
                          />
                        </div>

                        {/* A count in a cell is a question — which findings put
                            us there — and a matrix that cannot answer it is
                            decoration. */}
                        <div className="flex-1 min-w-0 xl:border-l xl:border-slate-800 xl:pl-6">
                          {!cell ? (
                            <div className="h-full flex items-center justify-center text-center px-4 py-6">
                              <p className="text-xs text-slate-600 max-w-[18rem]">
                                Select a cell to list the findings behind it.
                              </p>
                            </div>
                          ) : (
                            <div className="flex flex-col h-full">
                              <div className="flex items-baseline justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span
                                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                                    style={{ backgroundColor: BAND_FILL[cell.band] }}
                                  />
                                  <span className="text-xs font-medium text-slate-200 truncate">
                                    {BAND_LABEL[cell.band] || cell.band}
                                  </span>
                                  <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                    exposure {cell.exposure} × impact {cell.impact}
                                  </span>
                                </div>
                                {cellFindings?.length > 0 && (
                                  <button
                                    onClick={() => setExpanded((v) => !v)}
                                    title={expanded ? 'Collapse' : 'Expand'}
                                    aria-label={expanded ? 'Collapse' : 'Expand'}
                                    className="shrink-0 p-1 -m-1 text-slate-500 hover:text-slate-300"
                                  >
                                    {/* Points the way it moves: right to take the
                                        panel's full width, back left to return it. */}
                                    <svg
                                      className={`w-4 h-4 transition-transform duration-200 ${
                                        expanded ? 'rotate-90' : '-rotate-90'}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                )}
                              </div>

                              {cellFindings === null ? (
                                <div className="flex items-center gap-2 py-6 text-xs text-slate-600">
                                  <span className="animate-spin rounded-full h-3 w-3 border-b border-slate-500" />
                                  Loading findings…
                                </div>
                              ) : !cellFindings.length ? (
                                <p className="text-xs text-slate-600 py-6">
                                  No findings in this cell.
                                </p>
                              ) : (
                                <div
                                  className="space-y-1 overflow-y-auto pr-1"
                                  style={{ maxHeight: expanded ? '26rem' : '17rem' }}
                                >
                                  {cellFindings.map((f) => (
                                    <div
                                      key={f.id}
                                      className="px-2 py-1.5 rounded odd:bg-slate-800/20"
                                    >
                                      <div className={`flex gap-2 ${expanded ? 'items-start' : 'items-baseline'}`}>
                                        <span className={`text-[11px] font-medium tabular-nums shrink-0 ${
                                          SEVERITY_TONE[(f.severity || '').toLowerCase()] || 'text-slate-500'}`}>
                                          {f.code || '—'}
                                        </span>
                                        {/* Truncated only while narrow. Expanded, the
                                            whole title shows — a half-read title is the
                                            reason to expand in the first place. */}
                                        <span className={`text-xs text-slate-300 ${expanded ? '' : 'truncate'}`}>
                                          {f.title}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-600">
                                        <span>{f.engagement}</span>
                                        {f.type && <span className={expanded ? '' : 'truncate'}>· {f.type}</span>}
                                        {!f.remediated && (
                                          <span className="text-amber-500/80 shrink-0">· no remediation</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Panel>
                  </div>

                  <div className={`space-y-6 ${expanded ? 'hidden' : ''}`}>
                    <Panel title="By risk level">
                      <div className="space-y-2">
                        {Object.entries(bands).map(([band, count]) => (
                          <div key={band} className="flex items-center gap-3">
                            <span className="text-[11px] text-slate-400 w-24">
                              {BAND_LABEL[band] || band}
                            </span>
                            <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                  width: `${(count / bandMax) * 100}%`,
                                  backgroundColor: BAND_FILL[band],
                                }}
                              />
                            </div>
                            <span className="text-xs tabular-nums text-slate-400 w-8 text-right">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Panel>

                    <Panel title="Most frequent types">
                      {data.by_type?.length ? (
                        <div className="space-y-2">
                          {data.by_type.map((row) => (
                            <div key={row.type} className="flex items-center gap-3">
                              <span className="text-[11px] text-slate-400 flex-1 truncate" title={row.type}>
                                {row.type}
                              </span>
                              <div className="w-24 h-2 rounded-full bg-slate-800 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-slate-500"
                                  style={{ width: `${(row.count / data.by_type[0].count) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs tabular-nums text-slate-400 w-6 text-right">
                                {row.count}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-600">No data.</p>
                      )}
                    </Panel>
                  </div>
                </div>

                {data.history?.length > 1 && (
                  <Panel
                    title="Risk history"
                    aside={customerId ? 'per engagement, in chronological order'
                                      : 'pick a customer for a comparable trend'}
                  >
                    <RiskHistory points={data.history} />
                  </Panel>
                )}

                <Panel title="Engagements" aside="worst first">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <tr>
                          <th className="text-left font-medium py-2 pr-3">Engagement</th>
                          <th className="text-left font-medium py-2 px-3">Type</th>
                          <th className="text-left font-medium py-2 px-3">Status</th>
                          <th className="text-right font-medium py-2 px-3">Findings</th>
                          <th className="text-left font-medium py-2 px-3 w-40">Risk</th>
                          <th className="text-right font-medium py-2 px-3">No remediation</th>
                          <th className="text-left font-medium py-2 pl-3 w-32">Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {engagements.map((e) => (
                          <tr
                            key={e.id}
                            onClick={() => navigate(
                              e.wireless ? `/offensive/wireless/${e.id}` : `/offensive/operations/${e.id}`)}
                            className="border-b border-slate-800/60 hover:bg-slate-800/20 cursor-pointer transition-colors"
                          >
                            <td className="py-2 pr-3">
                              <div className="text-slate-200">{e.code}</div>
                              <div className="text-[11px] text-slate-500 truncate max-w-xs">{e.title}</div>
                            </td>
                            <td className="py-2 px-3 text-slate-400 text-[11px]">{e.subtype}</td>
                            <td className="py-2 px-3 text-slate-400 text-[11px]">{e.status}</td>
                            <td className="py-2 px-3 text-right tabular-nums text-slate-300">{e.findings}</td>
                            <td className="py-2 px-3">
                              {/* Proportional stack, so a row's risk shape is
                                  readable at a glance without five columns. */}
                              <div className="flex h-2 rounded-full overflow-hidden bg-slate-800">
                                {Object.entries(e.bands).map(([band, count]) => (
                                  count > 0 && (
                                    <div
                                      key={band}
                                      title={`${band}: ${count}`}
                                      style={{
                                        width: `${(count / Math.max(1, e.findings)) * 100}%`,
                                        backgroundColor: BAND_FILL[band],
                                      }}
                                    />
                                  )
                                ))}
                              </div>
                            </td>
                            <td className={`py-2 px-3 text-right tabular-nums ${
                              e.unremediated ? 'text-amber-400' : 'text-slate-500'}`}>
                              {e.unremediated || '—'}
                            </td>
                            <td className="py-2 pl-3">
                              {e.coverage ? (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden min-w-[48px]">
                                    <div
                                      className="h-full rounded-full bg-indigo-500"
                                      style={{ width: `${e.coverage.ratio * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-[11px] tabular-nums text-slate-500">
                                    {Math.round(e.coverage.ratio * 100)}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-700 text-[11px]">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default OffensiveDashboard;
